import * as React from 'react';
/**
 * @interface ModalProps
 * @description Define las propiedades del componente Modal.
 */
interface ModalProps {
  /**
   * @property {boolean} isOpen - Indica si el modal está abierto.
   */
  isOpen: boolean;
  /**
   * @property {function} onClose - La función de cierre del modal.
   */
  onClose: () => void;
  /**
   * @property {string} title - El título del modal.
   */
  title?: string;
  /**
   * @property {React.ReactNode} children - Los hijos del modal.
   */
  children: React.ReactNode;
  /**
   * @property {string} size - El tamaño del modal.
   */
  size?: 'sm' | 'md' | 'lg' | 'xl';
  /**
   * @property {boolean} showCloseButton - Indica si se debe mostrar el botón de cierre del modal.
   */
  showCloseButton?: boolean;
  /**
   * @property {boolean} bare - Sin relleno interno ni área de título: el contenido llega a los
   * bordes del modal (por ejemplo, una franja de color arriba). `title` y `showCloseButton` no
   * aplican; el contenido pone su propio encabezado. Por defecto `false`.
   */
  bare?: boolean;
}

/**
 * @component Modal
 * @description Un componente modal personalizable con opciones de tamaño, título y botón de cierre.
 * @param {ModalProps} props Las propiedades del componente.
 * @returns {React.ReactElement | null} El componente modal renderizado o nulo si no está abierto.
 */
export const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  title,
  children,
  size = 'md',
  showCloseButton = true,
  bare = false,
}) => {
  if (!isOpen) return null;

  const sizeClasses = {
    sm: 'max-w-md',
    md: 'max-w-lg',
    lg: 'max-w-2xl',
    xl: 'max-w-4xl',
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        {/* Overlay */}
        <div
          className="fixed inset-0 bg-gray-500/75 transition-opacity"
          onClick={onClose}
        />

        {/* Modal */}
        <div
          className={`relative z-10 inline-block align-bottom bg-background-primary rounded-lg text-left overflow-hidden shadow-xl transition-all sm:my-8 sm:align-middle w-full ${sizeClasses[size]}`}
        >
          {title && !bare && (
            <div className="bg-background-primary px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg leading-6 font-medium text-gray-900">
                  {title}
                </h3>
                {showCloseButton && (
                  <button
                    onClick={onClose}
                    className="text-gray-400 hover:text-gray-600 focus:outline-none focus:text-gray-600"
                  >
                    <svg
                      className="h-6 w-6"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </button>
                )}
              </div>
            </div>
          )}
          {bare ? (
            children
          ) : (
            <div className="bg-background-primary px-4 pt-5 pb-4 sm:p-6">
              {children}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
